import React, { useContext, useMemo, useState } from "react";
import "./lesson-details-header.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { icons } from "../../../../../statics/icons";
import { useTranslation } from "react-i18next";
import {
  formatDate,
  showPopupToastMsg,
  showToastMsg,
} from "../../../../../helpers";
import { UserRoles } from "../../../../../types/userRoles";
import ActionMenuComponent from "../../../../reusable-components/components/menu.component";
import { ConfirmDialog } from "../../../../../dialogs/confirm-dialog/confirm-dialog";
import { history } from "react-router-guard";
import { services } from "../../../../../services";
import { educationRoutes } from "../../../types/education.routes";
import CreateLessonDialog from "../../../modals/create-lesson/create-lesson.dialog";
import { GlobalContext } from "../../../../../store";
import { LessonDetails, LessonStatus } from "../../../types/lesson.types";

const LessonDetailsHeaderComponent = ({
  lessonId,
  lessonDetails,
  loading,
  duration,
  onEdit,
}: {
  lessonId: string;
  lessonDetails: LessonDetails;
  loading: boolean;
  duration: string;
  onEdit: () => void;
}) => {
  const [t] = useTranslation();

  const { user } = useContext(GlobalContext);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const actionItems = useMemo(
    () => [
      {
        label: t("EDIT"),
        onClick: () => setShowEditModal(true),
      },
      {
        label: t("DELETE"),
        onClick: () => setShowConfirmModal(true),
      },
    ],
    [t],
  );

  const deleteLesson = async () => {
    try {
      const response = await services.courses.deleteLesson(lessonId);
      if (response && response.message === "SUCCESS") {
        showToastMsg(
          "success",
          t("EDUCATION_PAGE.LESSON_DELETED_SUCCESSFULLY"),
        );
        history.push(educationRoutes.course(lessonDetails?.course?.id));
      } else if (response?.message) {
        showPopupToastMsg("error", t(response?.message));
      } else {
        showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
      }
    } catch (error) {
      console.error("Error deleting lesson:", error);
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    }
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    setShowConfirmModal(false);
    if (confirmed) {
      deleteLesson();
    }
  };

  const handleEditModalClose = (value: boolean) => {
    setShowEditModal(false);
    if (value) {
      onEdit();
    }
  };

  return (
    <Box>
      {/* Lesson Details Header */}
      <Box
        className="d-flex justify-content-between mb-4 "
        style={{ gap: "1.9rem" }}
      >
        <Box className="d-flex" style={{ gap: "0.5rem" }}>
          {!loading &&
          lessonDetails?.userStatus?.status === LessonStatus.COMPLETED ? (
            <Box className="d-flex">
              <img
                src={icons.PAGES.EDUCATION.COMPLETED}
                alt="completed"
                width={40}
                height={40}
              />
            </Box>
          ) : null}
          <Box className="info">
            {loading ? (
              <Skeleton variant="text" height={20} width={350} />
            ) : (
              <Typography variant="h1" color="textPrimary">
                {lessonDetails?.title || ""}
              </Typography>
            )}
            <Box
              className="d-flex align-items-center"
              style={{ gap: "1.9rem" }}
            >
              {loading ? (
                <Skeleton variant="text" height={20} width={100} />
              ) : (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className="d-flex align-items-center"
                  style={{ gap: "0.5rem" }}
                >
                  <img src={icons.CALENDER} alt="calender" />{" "}
                  {formatDate(lessonDetails?.createdAt, "ddd, MMM DD, HH:mm Z")}
                </Typography>
              )}
              {loading ? (
                <Skeleton variant="text" height={20} width="60%" />
              ) : (
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className="d-flex align-items-center"
                  style={{ gap: "0.5rem" }}
                >
                  <img src={icons.clock} alt="clock" />{" "}
                  {t("EDUCATION_PAGE.DURATION")}: {duration} min.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {UserRoles.FULL_ACCESS_ROLES.includes(user.role) && !loading && (
          <Box className="actions">
            <ActionMenuComponent items={actionItems} />
            {showConfirmModal && (
              <ConfirmDialog
                show={showConfirmModal}
                onClose={handleConfirmDelete}
                dialogTitle={t("EDUCATION_PAGE.CONFIRM_DELETE")}
                dialogBody={t(
                  "EDUCATION_PAGE.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_LESSON",
                )}
                confirmBtnText={t("DELETE")}
                cancelBtn={true}
              />
            )}

            {showEditModal && (
              <CreateLessonDialog
                show={showEditModal}
                courseId={Number(lessonDetails?.course?.id)}
                onClose={handleEditModalClose}
                lessonId={Number(lessonId)}
                lessonData={lessonDetails}
              />
            )}
          </Box>
        )}
      </Box>
      {/* Lesson Description */}
      <Box className="mb-4">
        {loading ? (
          <>
            <Skeleton variant="text" height={18} width={550} />
            <Skeleton variant="text" height={18} width={400} />
            <Skeleton variant="text" height={18} width={450} />
            <Skeleton variant="text" height={18} width={550} />
          </>
        ) : (
          <Typography variant="body1" className="w-100" color="textPrimary">
            {lessonDetails?.description || ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LessonDetailsHeaderComponent;
